import { Route, Switch, useHistory } from 'react-router-dom';
import { useEffect } from 'react';
import { useModal } from 'react-modal-hook';
import { useTranslation } from 'react-i18next';

import { AddTicketMutation as AddTicket, AddTicketCategoryMutation as AddTicketCategory } from '__generated__/graphql';
import { assetUrl } from '../../../../common/helpers';
import AddTicketCategoryForm from './AddTicketCategoryForm';
import AddTicketForm from './AddTicketForm';
import EditTicketCategoryForm from './EditTicketCategoryForm';
import GetTicketQuery from './GetTicketQuery';
import GetTicketsQuery from './GetTicketsQuery';
import TicketList from './TicketList';
import TicketSorter from './TicketSorter';
import UI from '../../../../common/UI';
import useProject from '../../../useProject';
import useQuery from '../../../../api/useQuery';
import useScroll from '../../../../common/useScroll';
import useUrlState from '../../../../common/useUrlState';

type AddedTicket = AddTicket['addTicket'];

export interface TicketsPageProps {
  eventId: string;
  ticketId?: string;
}

const TicketsPage = ({ eventId, ticketId }: TicketsPageProps) => {
  const { t } = useTranslation();
  const history = useHistory();
  const project = useProject();
  const { scrollToIfInvisible } = useScroll();

  const { data, refetch } = useQuery(
    GetTicketsQuery,
    {
      variables: {
        project_id: project.id,
        event_id: eventId,
      },
    },
  );

  const event = data?.project.event;
  const distanceUnits = data?.distanceUnits;

  const { data: ticketData, loading: ticketLoading } = useQuery(
    GetTicketQuery,
    {
      variables: {
        event_id: eventId,
        ticket_id: ticketId,
      },
      skip: !ticketId || ticketId === 'sort' || ticketId === 'categories',
      nextFetchPolicy: 'network-only',
    },
  );

  const handleSuccess = () => {
    history.push(`/${project.id}/events/${event.id}/tickets`);
    scrollToIfInvisible('EventTab');
  };

  const [openAddTicketCategoryModal, closeAddTicketCategoryModal] = useModal(() => {
    const handleSuccess = (ticketCategory: AddTicketCategory['addTicketCategory']) => {
      closeAddTicketCategoryModal();

      window.setTimeout(() => {
        scrollToIfInvisible(`TicketCategory_${ticketCategory.id}`);
      });
    };

    return (
      <UI.Modal maxWidth={600} close={closeAddTicketCategoryModal}>
        <AddTicketCategoryForm event={event} onSuccess={handleSuccess} />
      </UI.Modal>
    );
  }, [event]);

  const [urlState] = useUrlState({
    parse: (rawParams, { string }) => ({
      ticket_category: string(rawParams.ticket_category),
    }),
  });

  const [openAddTicketModal, closeAddTicketModal] = useModal(() => {
    const handleSuccess = (ticket: AddedTicket) => {
      closeAddTicketModal();
      history.push(`/${project.id}/events/${event.id}/tickets/${ticket.id}`);
    };

    const handleClose = () => {
      closeAddTicketModal();
      history.replace(`/${project.id}/events/${event.id}/tickets`);
    };

    const ticketCategory = event.ticket_categories.find(
      (ticketCategory) => ticketCategory.id === urlState.ticket_category,
    );

    return (
      <UI.Modal maxWidth={600} close={handleClose}>
        <AddTicketForm
          event={event}
          ticketCategory={ticketCategory}
          distanceUnits={distanceUnits}
          onSuccess={handleSuccess}
        />
      </UI.Modal>
    );
  }, [event, urlState.ticket_category, distanceUnits]);

  useEffect(() => {
    refetch();
  }, [ticketId, refetch]);

  useEffect(() => {
    if (event && ticketId === 'add') {
      openAddTicketModal();
    }
  }, [event, ticketId, openAddTicketModal]);

  if (!event) {
    return <UI.PageLoader />;
  }

  const ticketCategoriesCount = event.ticket_categories.length;
  const ticketsCount = event.ticket_categories.reduce((sum, ticketCategory) => sum + ticketCategory.tickets.length, 0);

  return (
    <UI.Section>
      <Switch>
        <Route
          path={`/${project.id}/events/${eventId}/tickets/categories/:ticketCategoryId`}
          render={({ match: { params: { ticketCategoryId } } }) => {
            const ticketCategory = event.ticket_categories.filter(
              (ticketCategory) => ticketCategory.id === ticketCategoryId,
            )[0];

            if (!ticketCategory) {
              return (
                <UI.PageLoader />
              );
            }

            return (
              <EditTicketCategoryForm
                ticketCategory={ticketCategory}
                event={event}
                onSuccess={handleSuccess}
              />
            );
          }}
        />

        <Route path={`/${project.id}/events/${eventId}/tickets/sort`}>
          <TicketSorter event={event} onSuccess={handleSuccess} />
        </Route>

        <Route>
          <UI.GridContainer>
            {ticketCategoriesCount > 0 && (
              <UI.FlexContainer sc={{ justifyContent: 'space-between', direction: ['column', 'row'] }}>
                <UI.SectionHeading>
                  {t('tickets')}
                </UI.SectionHeading>

                {ticketCategoriesCount + ticketsCount > 1 && (
                  <UI.FlexContainer sc={{ alignItems: 'center', spaceX: 1, spaceY: [1, 0], wrap: 'wrap' }}>
                    <UI.Button
                      onClick={openAddTicketCategoryModal}
                      sc={{ brand: 'secondary' }}
                    >
                      {t('add_ticket_category')}
                    </UI.Button>
                    <UI.Button
                      onClick={() => openAddTicketModal()}
                      sc={{ brand: 'secondary' }}
                    >
                      {t('add_ticket')}
                    </UI.Button>
                    {ticketCategoriesCount + ticketsCount > 2 && (
                      <UI.NavButton
                        to={`/${project.id}/events/${event.id}/tickets/sort`}
                      >
                        {t('sort_tickets')}
                      </UI.NavButton>
                    )}
                  </UI.FlexContainer>
                )}
              </UI.FlexContainer>
            )}

            {ticketCategoriesCount + ticketsCount > 1 && event.ticket_categories.map((ticketCategory) => (
              <UI.GridContainer key={ticketCategory.id}>
                <TicketList
                  event={event}
                  ticketCategory={ticketCategory}
                  activeTicketId={ticketId}
                  // Always pass a fresh data object
                  ticketData={ticketData?.ticket.id === ticketId && !ticketLoading ? ticketData : null}
                  openAddTicketModal={() => openAddTicketModal()}
                />
              </UI.GridContainer>
            ))}

            {ticketCategoriesCount === 1 && ticketsCount === 0 && (
              <UI.Card sc={{ padding: 8, textAlign: 'center', background: 'gray.50' }} style={{ textWrap: 'balance' }}>
                <UI.Div sc={{ maxWidth: [undefined, '60%'] }} style={{ margin: '0 auto' }}>
                  <img src={assetUrl('images/ticket.svg')} alt="" height={120} />
                  <UI.H3 sc={{ mb: 2 }}>{t('add_tickets')}</UI.H3>
                  <UI.Paragraph>{t('tickets_get_started')}</UI.Paragraph>
                  <UI.Button
                    onClick={openAddTicketModal}
                    sc={{ brand: 'secondary' }}
                  >
                    {t('add_a_ticket')}
                  </UI.Button>
                </UI.Div>
              </UI.Card>
            )}
          </UI.GridContainer>
        </Route>
      </Switch>
    </UI.Section>
  );
};

export default TicketsPage;
